// @flow

import { toUpper, maxBy, orderBy, range, sortBy } from 'lodash';
import moment from 'moment';
import { type Receiver } from 'Common/auth/types';
import type { Timer, NewTimer, timerSortOption } from './types';

// number of days for Daily Schedule carousel rendering
const NUMBER_OF_DAYS_TO_RENDER = 8;
// List of days for Daily Schedule
export const generateDatesArray = () =>
  range(0, NUMBER_OF_DAYS_TO_RENDER).map((i, idx) =>
    moment()
      .startOf('day')
      .subtract(-idx, 'days'),
  );

export const sortTimersAlphabetically = (timers: Timer[]): Timer[] =>
  sortBy(timers, timer => toUpper(timer.event_name));

const getTimerStartTime = (timer: Timer) => {
  if (timer.tm_period == null || timer.tm_period.start_time == null) return 0;
  return new Date(timer.tm_period.start_time).getTime();
};

export const sortTimersByDate = (timers: Timer[]): Timer[] => sortBy(timers, getTimerStartTime);

export const sortTimersByPriority = (timers: Timer[]): Timer[] => {
  /* Lower priority is better - 96 is minimum priority */
  // to ensure that PTAT has the highest priority use a constant value for it
  const getPriority = timer => (timer.channel_name !== 'PTAT' ? timer.priority : -1);
  return sortBy(timers, getPriority);
};

const MIN_TIMER_PRIORITY = 96;

export const getLowestTimerPriority = (timers: Array<Timer>): number =>
  timers.length
    ? maxBy(timers, (timer: Timer) => timer.priority).priority || MIN_TIMER_PRIORITY
    : MIN_TIMER_PRIORITY;

const updateTimersOnPriorityChange = (
  timers: Array<Timer>,
  gTimerId: number,
  oldPriority: number,
  newPriority: number,
): Array<Timer> => {
  const priorityChangeDirection = newPriority < oldPriority ? 1 : -1;
  const minPriority = Math.min(oldPriority, newPriority);
  const maxPriority = Math.max(oldPriority, newPriority);
  return timers.map(timer => {
    if (
      timer.gtimer_id !== gTimerId &&
      timer.priority >= minPriority &&
      timer.priority <= maxPriority
    ) {
      return {
        ...timer,
        priority: timer.priority + priorityChangeDirection,
      };
    }
    return timer;
  });
};

export const mergeTimerUpdate = (timer: Timer, updatedTimer: NewTimer) => {
  const {
    duration,
    minutes_early: min_early = timer.min_early,
    minutes_late: min_late = timer.min_late,
    /* cut following props since they aren't compatible with the Timer type */
    channel_name: channelName,
    timer_type: timerType,
    start_time: startTime,
    receiver_id: receiverId,

    ...restOfUpdatedProps
  } = updatedTimer;

  return { ...timer, ...restOfUpdatedProps, min_early, min_late };
};

export const updateTimersList = (
  timers: Array<Timer>,
  updatedTimer: NewTimer,
  gTimerId: ?number,
): Array<Timer> => {
  let oldPriority;
  const resultTimers = timers.map((timer: Timer) => {
    if (typeof timer.gtimer_id === 'number') {
      if (timer.gtimer_id === gTimerId) {
        /* It is required to update timer in-place,
           because of Radish implementation of interaction with grasshopper service.
           Since timer-update operation can take up to several minutes and
           because Radish puts timers received from grasshopper into cache,
           "update-timer" request followed by immediate "get_timers" request
           won't result in receiving a list of updated timers. */
        oldPriority = timer.priority;
        return mergeTimerUpdate(timer, updatedTimer);
      }
    }
    return timer;
  });
  if (
    gTimerId != null &&
    typeof updatedTimer.priority === 'number' &&
    typeof oldPriority === 'number' &&
    updatedTimer.priority !== oldPriority
  ) {
    return updateTimersOnPriorityChange(resultTimers, gTimerId, oldPriority, updatedTimer.priority);
  }
  return resultTimers;
};

/*
 * Check if options keys '4' or '7' contain in current timer record,
 * that have been deleted due to DE8991 (https://rally1.rallydev.com/#/233447464096d/detail/defect/295609707988)
 */
export const isDeprecatedTimerFrequencyKey = (key: string | number): boolean =>
  key === '4' || key === 4 || key === '7' || key === 7;

export const orderSortedTimersBySortOption = (
  timers: Array<Timer>,
  currentSort: timerSortOption,
): Array<Timer> => {
  switch (currentSort.sortBy) {
    case 'title':
      return orderBy(
        [...timers],
        [timer => timer.event_name],
        [currentSort.orderBy === 'asc' ? 'asc' : 'desc'],
      );
    case 'date':
      return orderBy(
        [...timers],
        [timer => Number(moment(timer.tm_period.start_time).valueOf())],
        [currentSort.orderBy === 'asc' ? 'asc' : 'desc'],
      );
    case 'priority':
      return orderBy(
        [...timers],
        [timer => timer.priority],
        [currentSort.orderBy === 'asc' ? 'asc' : 'desc'],
      );
    default:
      return timers;
  }
};

export const isReceiverOnline = (receiverId: string, receivers: Array<Receiver>): boolean =>
  receivers.find(r => r.receiver_id === receiverId)?.state === 'online';
